import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/** Mobile breakpoint used by the Horizon drawer layout. */
const MOBILE_DRAWER_QUERY = window.matchMedia('(max-width: 989px)');
/** Safety net for browsers that occasionally fail to resolve a transition promise. */
const CLOSE_FALLBACK_MS = 520;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number} */
  #operationId = 0;

  /** @type {number | null} */
  #closeFallbackTimer = null;

  /** @type {number} */
  #lockedScrollY = 0;

  /** @type {boolean} */
  #ownsBodyScrollLock = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagehide', this.#onPageHide);
    MOBILE_DRAWER_QUERY.addEventListener('change', this.#onBreakpointChange);

    // A cached page must never come back with an invisible backdrop or a stale scroll lock.
    if (!this.isOpen) this.#cleanupGlobalDrawerState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPageHide);
    MOBILE_DRAWER_QUERY.removeEventListener('change', this.#onBreakpointChange);
    this.#clearCloseFallback();
    this.#unlockBodyScroll();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.#close(this.#getDetailsElement(event));
  };

  /** Clean up stale states restored from the back-forward cache. */
  #onPageShow = (event) => {
    if (event.persisted) this.#closeImmediately();
  };

  /** Release the body before the browser stores or navigates away from this page. */
  #onPageHide = () => {
    this.#unlockBodyScroll();
  };

  /** Close the mobile drawer when switching to the desktop navigation layout. */
  #onBreakpointChange = (event) => {
    if (!event.matches && this.isOpen) this.#closeImmediately();
  };

  /** @returns {boolean} Whether the main menu drawer is open. */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   * The native summary toggle is prevented so the component owns the complete state change.
   * This removes a race that could leave the page scroll-locked on mobile Safari.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close(event) : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    const operationId = ++this.#operationId;
    this.#clearCloseFallback();

    // Set [open] synchronously instead of waiting for the browser's default summary action.
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#lockBodyScroll();
      document.documentElement.setAttribute('data-menu-drawer-open', '');
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (operationId !== this.#operationId || !details.hasAttribute('open')) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation before trapping focus, but ignore stale callbacks.
      const drawer = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (operationId !== this.#operationId || !details.hasAttribute('open')) return;
        trapFocus(details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the closest open submenu.
   * @param {Event} [event]
   */
  back(event) {
    event?.preventDefault();
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    const operationId = ++this.#operationId;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    const drawer = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu');
    let finalized = false;

    const finalize = () => {
      if (finalized || operationId !== this.#operationId) return;
      finalized = true;
      this.#clearCloseFallback();

      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        document.documentElement.removeAttribute('data-menu-drawer-open');
        this.#unlockBodyScroll();
        summary.focus({ preventScroll: true });
      } else {
        trapFocus(this.refs.details);
      }
    };

    // Prefer the real animation end, with a deterministic timeout as a Safari safety net.
    onAnimationEnd(drawer || details, finalize, { subtree: false });
    this.#closeFallbackTimer = window.setTimeout(finalize, CLOSE_FALLBACK_MS);
  }

  /** Close and reset without animation, used for bfcache and breakpoint cleanup. */
  #closeImmediately() {
    ++this.#operationId;
    this.#clearCloseFallback();
    removeTrapFocus();
    this.querySelectorAll('details[open], details.menu-open').forEach(reset);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    document.documentElement.removeAttribute('data-menu-drawer-open');
    this.#unlockBodyScroll();
  }

  /** Preserve the exact mobile scroll position while the menu is open. */
  #lockBodyScroll() {
    if (!MOBILE_DRAWER_QUERY.matches || this.#ownsBodyScrollLock) return;

    this.#lockedScrollY = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
    document.documentElement.style.setProperty('--menu-drawer-scroll-y', `${-this.#lockedScrollY}px`);
    document.body.classList.add('menu-drawer-scroll-locked');
    this.#ownsBodyScrollLock = true;
  }

  /** Restore the scroll position and remove every lock owned by this drawer. */
  #unlockBodyScroll() {
    if (!this.#ownsBodyScrollLock) {
      document.body.classList.remove('menu-drawer-scroll-locked');
      document.documentElement.style.removeProperty('--menu-drawer-scroll-y');
      return;
    }

    const scrollY = this.#lockedScrollY;
    document.body.classList.remove('menu-drawer-scroll-locked');
    document.documentElement.style.removeProperty('--menu-drawer-scroll-y');
    this.#ownsBodyScrollLock = false;

    requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' }));
  }

  #cleanupGlobalDrawerState() {
    document.documentElement.removeAttribute('data-menu-drawer-open');
    document.body.classList.remove('menu-drawer-scroll-locked');
    document.documentElement.style.removeProperty('--menu-drawer-scroll-y');
  }

  #clearCloseFallback() {
    if (this.#closeFallbackTimer === null) return;
    window.clearTimeout(this.#closeFallbackTimer);
    this.#closeFallbackTimer = null;
  }

  /**
   * Attach animationend listeners to animated menu items so temporary compositor
   * layers are removed after opening.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) element.classList.add('details-content--no-animation');
    });

    window.setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) element.classList.remove('details-content--no-animation');
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
}
